import React, { useRef, useEffect, useState } from "react";
import { Navigation } from "../Landing/components/Navigation";
import { Footer } from "../Landing/components/Footer";
import type { Page } from "../types/Page";
import { useHistory } from "react-router-dom";
import { blogsApi, type BlogPost } from "../../services/blogsApi";
import { useSecureVideoStream } from "../../hooks/useSecureVideoStream";

const VESPER_VIDEO_URL =
  "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260818_072341_50851634-bbc3-4c33-9acc-7647d4db44aa.mp4";

// Sample fallback posts if API has no published blogs
const SAMPLE_POSTS: BlogPost[] = [
  {
    id: 1,
    title: "Evaluating Multi-Modal LLMs in Clinical Radiology",
    slug: "evaluating-multimodal-llms-radiology",
    subtitle:
      "How board-certified radiologists benchmark vision-language models for edge-case detection and diagnosis reliability.",
    content: "",
    cover_image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=800&q=80",
    author_name: "Dr. Sarah Chen",
    created_at: new Date(Date.now() - 3 * 86400000).toISOString(),
    published_at: new Date(Date.now() - 3 * 86400000).toISOString(),
    is_published: true,
    tags: ["Clinical AI", "Radiology", "Evaluation"],
  },
  {
    id: 2,
    title: "Zero-Leakage Synthetic Data Generation for HIPAA Pipelines",
    slug: "zero-leakage-synthetic-data-hipaa",
    subtitle:
      "Architectural blueprint for privacy-preserving clinical data pipelines with differential privacy guarantees.",
    content: "",
    cover_image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=800&q=80",
    author_name: "Marcus Vance",
    created_at: new Date(Date.now() - 7 * 86400000).toISOString(),
    published_at: new Date(Date.now() - 7 * 86400000).toISOString(),
    is_published: true,
    tags: ["Privacy", "Security", "HIPAA"],
  },
  {
    id: 3,
    title: "Consensus Protocols for Multi-Specialist AI Verification",
    slug: "consensus-protocols-multispecialist-ai",
    subtitle:
      "Reducing inter-annotator disagreement through structured Delphi voting algorithms and calibrated confidence metrics.",
    content: "",
    cover_image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80",
    author_name: "Elena Rostova",
    created_at: new Date(Date.now() - 14 * 86400000).toISOString(),
    published_at: new Date(Date.now() - 14 * 86400000).toISOString(),
    is_published: true,
    tags: ["Operations", "Consensus", "Data Scale"],
  },
];

export const BlogListPage: Page = () => {
  const history = useHistory();
  const videoRef = useRef<HTMLVideoElement>(null);
  const blogHeroCardRef = useRef<HTMLDivElement>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  // Secure Video Stream with Chunked Delivery, Blob URL Isolation & Viewport Auto-Pause
  const { videoProps } = useSecureVideoStream(videoRef, {
    src: VESPER_VIDEO_URL,
    containerRef: blogHeroCardRef,
  });

  // Fetch blog posts
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const data = await blogsApi.getBlogPosts();
        if (data && data.length > 0) {
          setPosts(data);
        } else {
          setPosts(SAMPLE_POSTS);
        }
      } catch (error) {
        console.error("Failed to fetch blog posts, using fallback:", error);
        setPosts(SAMPLE_POSTS);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const scrollToArticles = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById("blog-posts-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="blog-page-root" style={{ minHeight: "100vh", width: "100%", backgroundColor: "#000000", color: "#ffffff", overflowX: "hidden", display: "flex", flexDirection: "column" }}>
      {/* 1. SOLID BLACK NAVBAR */}
      <Navigation />

      {/* Embedded Component Styles (Guarantees immediate zero-flash rendering) */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@1&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

        .blog-hero-wrapper {
          padding-top: clamp(140px, 16vh, 175px);
          padding-bottom: clamp(32px, 4.5vh, 56px);
          padding-left: clamp(16px, 3.5vw, 40px);
          padding-right: clamp(16px, 3.5vw, 40px);
          max-width: 1400px;
          margin-left: auto;
          margin-right: auto;
          width: 100%;
          box-sizing: border-box;
        }

        .blog-hero-card {
          position: relative;
          width: 100%;
          min-height: clamp(520px, 60vh, 620px);
          border-radius: clamp(20px, 2.5vw, 30px);
          overflow: hidden;
          background-color: #060707;
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: inset 0 1px 0 0 rgba(255, 255, 255, 0.08), 0 24px 60px -12px rgba(0, 0, 0, 0.95);
          display: flex;
          align-items: center;
        }

        .blog-hero-animation-box {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          width: 66%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
          z-index: 1;
        }

        .blog-hero-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center center;
          opacity: 0.95;
          filter: contrast(108%) brightness(98%);
          display: block;
        }

        .blog-hero-scrim {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 2;
          background: linear-gradient(
            90deg,
            #060707 0%,
            #060707 26%,
            rgba(6, 7, 7, 0.94) 44%,
            rgba(6, 7, 7, 0.5) 68%,
            rgba(6, 7, 7, 0.1) 88%,
            transparent 100%
          );
        }

        .blog-hero-bottom-scrim {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 2;
          background: linear-gradient(
            0deg,
            rgba(6, 7, 7, 0.7) 0%,
            transparent 35%
          );
        }

        .blog-hero-content {
          position: relative;
          z-index: 10;
          padding: clamp(36px, 5vw, 68px);
          max-width: clamp(520px, 54vw, 700px);
          display: flex;
          flex-direction: column;
          gap: clamp(16px, 2.2vh, 22px);
        }

        .vesper-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 6px;
          background: linear-gradient(90deg, #7d7d7d 0%, #2a2a2a 52%, #0a0a0a 100%);
          color: #f2f2f2;
          font-family: 'Inter', sans-serif;
          font-size: 12.5px;
          font-weight: 500;
          letter-spacing: -0.01em;
          width: fit-content;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
        }

        .badge-star {
          width: 16px;
          height: 18px;
          fill: #ffffff;
          filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.45));
          flex-shrink: 0;
        }

        .blog-hero-headline {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          letter-spacing: -0.04em;
          line-height: 1.12;
          color: #ffffff;
          font-size: clamp(34px, 4.2vw, 54px);
          margin: 0;
        }

        .blog-hero-headline em {
          font-family: 'Instrument Serif', Georgia, serif;
          font-style: italic;
          font-weight: 400;
          font-size: 1.1em;
          letter-spacing: -0.03em;
          color: #9a9a9a;
        }

        .blog-hero-lede {
          font-family: 'Inter', sans-serif;
          font-size: 15.5px;
          line-height: 1.6;
          color: #9a9a9a;
          max-width: 500px;
          margin: 0;
          letter-spacing: -0.015em;
        }

        .blog-hero-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          padding-top: 4px;
        }

        .vesper-btn-solid {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 42px;
          padding: 0 20px;
          border-radius: 6px;
          font-family: 'Inter', sans-serif;
          font-size: 13.5px;
          font-weight: 600;
          letter-spacing: -0.02em;
          line-height: 1;
          white-space: nowrap;
          cursor: pointer;
          text-decoration: none;
          background: linear-gradient(180deg, #ffffff 0%, #e7e7e7 48%, #cfcfcf 100%);
          color: #111111;
          border: 1px solid #ffffff;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95), 0 4px 14px rgba(0, 0, 0, 0.3);
          transition: all 0.35s ease;
        }

        .vesper-btn-solid:hover {
          background: linear-gradient(180deg, #ffffff 0%, #f3f6ff 42%, #d5def2 100%);
          border-color: #f2f6ff;
          box-shadow: inset 0 1px 0 #ffffff, 0 0 24px rgba(186, 208, 255, 0.4), 0 8px 18px rgba(255, 255, 255, 0.14);
        }

        .vesper-btn-ghost {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 42px;
          padding: 0 20px;
          border-radius: 6px;
          font-family: 'Inter', sans-serif;
          font-size: 13.5px;
          font-weight: 600;
          letter-spacing: -0.02em;
          line-height: 1;
          white-space: nowrap;
          cursor: pointer;
          text-decoration: none;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.12), rgba(0, 0, 0, 0.5) 46%, rgba(150, 170, 200, 0.1));
          color: #ffffff;
          border: 1px solid rgba(198, 198, 198, 0.55);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
          transition: all 0.35s ease;
        }

        .vesper-btn-ghost:hover {
          background: linear-gradient(135deg, rgba(210, 225, 255, 0.18), rgba(0, 0, 0, 0.35) 48%, rgba(180, 195, 220, 0.16));
          border-color: rgba(220, 230, 255, 0.8);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 0 20px rgba(170, 200, 255, 0.28);
        }

        .blog-hero-stats {
          display: flex;
          align-items: center;
          gap: 16px;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: #d8d8d8;
          padding-top: 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          flex-wrap: wrap;
        }

        .vesper-stat {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          letter-spacing: -0.015em;
          white-space: nowrap;
        }

        .vesper-stat-divider {
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.25);
        }

        /* 3. Main Content: Blog Posts Section */
        .blog-posts-section {
          position: relative;
          z-index: 10;
          padding: 64px clamp(16px, 3.5vw, 40px) 120px;
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
          box-sizing: border-box;
        }

        .blog-posts-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          padding-bottom: 24px;
          margin-bottom: 40px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .blog-posts-heading {
          font-family: 'Inter', sans-serif;
          font-size: clamp(24px, 2.5vw, 32px);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: #ffffff;
          margin: 0;
        }

        .blog-posts-sub {
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px;
          color: #9a9a9a;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .blog-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 28px;
        }

        .blog-card {
          background: #0c0c0c;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 32px 28px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
          position: relative;
          overflow: hidden;
        }

        .blog-card:hover {
          background: rgba(255, 255, 255, 0.02);
          border-color: rgba(255, 255, 255, 0.22);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 16px 36px -8px rgba(0, 0, 0, 0.8);
        }

        .blog-card-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          filter: grayscale(100%) opacity(12%);
          transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 0;
        }

        .blog-card:hover .blog-card-bg {
          filter: grayscale(0%) opacity(35%);
          transform: scale(1.03);
        }

        .blog-card-content {
          position: relative;
          z-index: 10;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 100%;
        }

        .blog-card-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
        }

        .blog-tag {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.08);
          padding: 3px 8px;
          border-radius: 4px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .blog-date {
          color: #9a9a9a;
        }

        .blog-card-title {
          font-family: 'Inter', sans-serif;
          font-size: 21px;
          font-weight: 700;
          line-height: 1.35;
          color: #ffffff;
          margin: 0 0 12px;
          letter-spacing: -0.015em;
        }

        .blog-card-desc {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          line-height: 1.6;
          color: #9a9a9a;
          margin: 0 0 24px;
        }

        .blog-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          padding-top: 16px;
          font-size: 13px;
        }

        .blog-card-author {
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }

        .blog-card-read {
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px;
          color: #9a9a9a;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s ease;
        }

        .blog-card:hover .blog-card-read {
          color: #ffffff;
          transform: translateX(3px);
        }

        /* Mobile Viewport Adaptations */
        @media (max-width: 960px) {
          .blog-hero-card {
            flex-direction: column;
            align-items: flex-start;
            min-height: auto;
            padding-bottom: 24px;
          }

          .blog-hero-animation-box {
            position: relative;
            width: 100%;
            height: 220px;
            order: 2;
          }

          .blog-hero-scrim {
            background: linear-gradient(
              180deg,
              rgba(6, 7, 7, 0.2) 0%,
              rgba(6, 7, 7, 0.85) 60%,
              #060707 100%
            );
          }

          .blog-hero-content {
            max-width: 100%;
            order: 1;
            padding: 32px 24px 16px;
          }

          .blog-hero-stats {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .vesper-stat-divider {
            display: none;
          }

          .blog-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* 2. CURVED HERO CARD: HEADING AND ANIMATION HERE (IMAGE 2 WIREFRAME) */}
      <section className="blog-hero-wrapper">
        <div
          ref={blogHeroCardRef}
          className="blog-hero-card"
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* Confined Vesper Animation Box */}
          <div className="blog-hero-animation-box">
            <video
              ref={videoRef}
              className="blog-hero-video"
              {...videoProps}
            />
            {/* Seamless Scrims */}
            <div className="blog-hero-scrim" />
            <div className="blog-hero-bottom-scrim" />
          </div>

          {/* Left Text Content inside Curved Hero Card */}
          <div className="blog-hero-content">
            {/* H1 with masked lines and italic em */}
            <h1 className="blog-hero-headline">
              <span style={{ display: "block" }}>
                Insights on <em>AI agents</em>,
              </span>
              <span style={{ display: "block" }}>
                data, and scale.
              </span>
            </h1>

            {/* Lede Description */}
            <p className="blog-hero-lede">
              Deploy adaptive AI agents that learn, execute, and scale operational tasks across your business.
            </p>

            {/* Actions: Liquid Glass Language */}
            <div className="blog-hero-actions">
              <a
                href="#blog-posts-section"
                onClick={scrollToArticles}
                className="vesper-btn-solid"
              >
                <span>Start for Free</span>
              </a>
            </div> 
          </div>
        </div>
      </section>

      {/* 3. MAIN CONTENT: BLOG POSTS LIST (IMAGE 2 WIREFRAME) */}
      <section className="blog-posts-section" id="blog-posts-section">
        <div className="blog-posts-header">
          <div>
            <h2 className="blog-posts-heading">Latest Dispatches</h2>
          </div>
          <div className="blog-posts-sub">// Engineering &amp; Research</div>
        </div>

        {loading ? (
          <div className="text-white text-center font-mono py-16">Loading articles...</div>
        ) : (
          <div className="blog-grid">
            {posts.map((post) => (
              <article
                key={post.id}
                onClick={() => history.push(`/blog/${post.slug}`)}
                className="blog-card"
              >
                {post.cover_image && (
                  <img src={post.cover_image} alt="" className="blog-card-bg" />
                )}
                <div className="blog-card-content">
                  <div>
                    <div className="blog-card-meta">
                      {post.tags && post.tags.length > 0 && (
                        <span className="blog-tag">{post.tags[0]}</span>
                      )}
                      <span className="blog-date">
                        {new Date(post.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>

                    <h3 className="blog-card-title">{post.title}</h3>
                    <p className="blog-card-desc">
                      {post.subtitle ||
                        "Deep dive into model architectures, evaluation benchmarks, and scalable labeling pipelines."}
                    </p>
                  </div>

                  <div className="blog-card-footer">
                    <span className="blog-card-author">{post.author_name}</span>
                    <span className="blog-card-read">Read Article →</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* 4. FOOTER */}
      <Footer />
    </div>
  );
};

BlogListPage.title = "Blog";
BlogListPage.path = "/blog";
BlogListPage.exact = true;

export default BlogListPage;
